<template>
  <section class="eva-page">
    <v-row>
      <v-col cols="12" offset="0" md="10" offset-md="1">
        <div class="eva-top">
          <v-breadcrumbs
            large
            :items="crumbs"
            divider="/"
            class="pl-0 eva-breadcrumbs"
          />

          <div class="eva-titlebar">
            <div class="eva-title">
              <v-icon color="primary" class="mr-2">mdi-school</v-icon>
              <span class="primary--text">Listagem de formações</span>
            </div>
          </div>
        </div>

        <v-card class="eva-card mt-6" elevation="10">
          <v-card-title class="eva-card__title primary white--text">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="white">mdi-school-outline</v-icon>
              <span class="font-weight-bold">Formações</span>
            </div>

            <v-spacer />

            <v-chip color="white" outlined class="eva-chip">
              <v-icon left small>mdi-format-list-bulleted</v-icon>
              {{ total }} registos
            </v-chip>
          </v-card-title>

          <v-card-text class="pt-6">
            <v-row class="mb-3" align="center">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="filter.search"
                  label="Pesquisar"
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  clearable
                  hide-details
                  @keyup.enter="searchFormacoes({ type: 'submit' })"
                  @click:clear="clearSearch"
                />
              </v-col>

              <v-col cols="12" md="6" class="d-flex justify-end">
                <v-btn
                  text
                  small
                  class="mr-2"
                  @click="resetFormacoes"
                >
                  <v-icon left small>mdi-filter-remove</v-icon>
                  Limpar
                </v-btn>

                <v-btn
                  color="primary"
                  small
                  @click="searchFormacoes({ type: 'submit' })"
                >
                  <v-icon left small>mdi-magnify</v-icon>
                  Procurar
                </v-btn>
              </v-col>
            </v-row>

            <div style="position:relative">
              <v-data-table
                :headers="headers"
                :items="items"
                :server-items-length="total"
                :loading="loading"
                :footer-props="tableFooter"
                :options.sync="options"
                class="eva-table"
                locale="pt-pt"
                disable-sort
              >
                <template v-slot:top>
                  <div class="d-flex align-center justify-space-between px-4 py-2">
                    <div class="text-subtitle-1 font-weight-bold">
                      Formações
                    </div>

                    <v-btn class="primary" small @click="$router.push('trainings/create')">
                      <v-icon left>mdi-plus</v-icon>
                      Adicionar
                    </v-btn>
                  </div>
                </template>

                <template v-slot:header.numero_acao>
                  <span class="sortable-header" @click="toggleSort('numero_acao')">
                    Nº Ação
                    <v-icon small>{{ getSortIcon('numero_acao') }}</v-icon>
                  </span>
                </template>

                <template v-slot:header.objetivo>
                  <span class="sortable-header" @click="toggleSort('objetivo')">
                    Objetivo
                    <v-icon small>{{ getSortIcon('objetivo') }}</v-icon>
                  </span>
                </template>

                <template v-slot:header.tipo_acao>
                  <span class="sortable-header" @click="toggleSort('tipo_acao')">
                    Tipo de Ação
                    <v-icon small>{{ getSortIcon('tipo_acao') }}</v-icon>
                  </span>
                </template>

                <template v-slot:header.status>
                  <span class="sortable-header" @click="toggleSort('status')">
                    Estado
                    <v-icon small>{{ getSortIcon('status') }}</v-icon>
                  </span>
                </template>

                <template v-slot:header.obra>
                  <span class="sortable-header" @click="toggleSort('obra')">
                    Obra
                    <v-icon small>{{ getSortIcon('obra') }}</v-icon>
                  </span>
                </template>

                <template v-slot:header.data>
                  <span class="sortable-header" @click="toggleSort('data')">
                    Data
                    <v-icon small>{{ getSortIcon('data') }}</v-icon>
                  </span>
                </template>

                <template v-slot:header.participantes_count>
                  <span class="sortable-header" @click="toggleSort('participantes_count')">
                    Participantes
                    <v-icon small>{{ getSortIcon('participantes_count') }}</v-icon>
                  </span>
                </template>

                <template v-slot:header.created_at>
                  <span class="sortable-header" @click="toggleSort('created_at')">
                    Criado em
                    <v-icon small>{{ getSortIcon('created_at') }}</v-icon>
                  </span>
                </template>

                <template v-slot:item.numero_acao="{ item }">
                  {{ item.numero_acao || "—" }}
                </template>

                <template v-slot:item.objetivo="{ item }">
                  {{ getObjetivo(item) }}
                </template>

                <template v-slot:item.tipo_acao="{ item }">
                  {{ getTipoAcaoLabel(item.tipo_acao) }}
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    small
                    :color="getStatusColor(item.status)"
                    dark
                  >
                    {{ getStatusLabel(item.status) }}
                  </v-chip>
                </template>

                <template v-slot:item.obra="{ item }">
                  <span v-if="item.obra">
                    {{ item.obra.Codigo ? item.obra.Codigo + " - " : "" }}{{ item.obra.Nome || item.obra.name || item.obra.Descricao || "" }}
                  </span>
                  <span v-else>
                    —
                  </span>
                </template>

                <template v-slot:item.data="{ item }">
                  {{ getTableDate(item.data) }}
                </template>

                <template v-slot:item.participantes_count="{ item }">
                  {{ item.participantes ? item.participantes.length : item.participantes_count || 0 }}
                </template>

                <template v-slot:item.created_at="{ item }">
                  {{ getTableDate(item.created_at) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-row align="center" justify="center">
                    <v-col cols="auto">
                      <v-icon
                        small
                        color="primary"
                        class="mr-2"
                        @click="$router.push('trainings/' + item.id)"
                      >
                        mdi-eye
                      </v-icon>

                      <IconRemove
                        :disabled="item.status === 'completed'"
                        v-if="$store.getters.hasPermission(['super', 'trainings.delete'])"
                        class="mr-2"
                        @on-submit="deleteFormacao(item)"
                      />
                    </v-col>
                  </v-row>
                </template>

                <template v-slot:no-data>
                  <div class="pa-6 grey--text text--darken-1">
                    Sem resultados.
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import Formacoes from "@/api/Formacoes.js";
import dayjs from "dayjs";
import IconRemove from "@/components/UI/IconRemove.vue";

export default {
  components: {
    IconRemove,
  },

  watch: {
    options: {
      handler() {
        if (this.skipOptionsWatch) {
          return;
        }

        setTimeout(() => {
          this.searchFormacoes();
        }, 10);
      },
      deep: true,
    },
  },

  beforeCreate() {
    document.title = "ODC - Listagem de formações";
  },

  data: () => ({
    headers: [
      { text: "Nº Ação", sortable: false, value: "numero_acao" },
      { text: "Objetivo", sortable: false, value: "objetivo" },
      { text: "Tipo de Ação", sortable: false, value: "tipo_acao" },
      { text: "Estado", sortable: false, value: "status" },
      { text: "Obra", sortable: false, value: "obra" },
      { text: "Data", sortable: false, value: "data" },
      { text: "Participantes", sortable: false, value: "participantes_count" },
      { text: "Criado em", sortable: false, value: "created_at" },
      { text: "Opções", align: "center", sortable: false, value: "actions" },
    ],

    items: [],
    total: 0,
    loading: false,
    searching: false,
    skipOptionsWatch: false,

    tableFooter: {
      "items-per-page-options": [5, 10, 15, 20],
      "items-per-page-text": "Linhas por página",
    },

    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
    },

    filter: {
      search: null,
    },
  }),

  computed: {
    crumbs() {
      return [
        { text: "Dashboard", disabled: false, to: "/admin", exact: true },
        {
          text: "Listagem de formações",
          disabled: true,
          to: window.location.href,
          exact: true,
        },
      ];
    },
  },

  mounted() {
    if (!this.$store.getters.hasPermission(["super", "trainings.read"])) {
      this.$router.push("/admin");
    }

    this.fillBaseData();
    this.searchFormacoes();
  },

  methods: {
    fillBaseData() {
      const key = "formacoes-list-" + this.$store.state.user.data.id;

      if (localStorage[key] !== undefined) {
        let data = JSON.parse(localStorage[key]);

        this.filter = data.filter || {
          search: null,
        };

        this.options = data.options || {
          page: 1,
          itemsPerPage: 10,
          sortBy: [],
          sortDesc: [],
        };
      }
    },

    searchFormacoes(event) {
      if (!!event && event.type === "submit") {
        this.options.page = 1;
      }

      this.loading = true;

      let request = {
        page: this.options.page,
        limit: this.options.itemsPerPage,
        search: this.filter.search || null,
        sortBy: this.getSortBy(),
        sortDesc: this.getSortDesc(),
      };

      localStorage[
        "formacoes-list-" + this.$store.state.user.data.id
      ] = JSON.stringify({
        filter: this.filter,
        options: this.options,
      });

      Formacoes.search(request)
        .then((response) => {
          let data = response.data;

          this.total = data.total;
          this.items = data.data || [];
          this.searching = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    toggleSort(column) {
      const currentSort = this.getSortBy();
      const currentDesc = this.getSortDesc();

      this.skipOptionsWatch = true;

      if (currentSort !== column) {
        this.options.sortBy = [column];
        this.options.sortDesc = [false];
      } else if (currentSort === column && currentDesc === 0) {
        this.options.sortBy = [column];
        this.options.sortDesc = [true];
      } else {
        this.options.sortBy = [];
        this.options.sortDesc = [];
      }

      this.options.page = 1;

      this.$nextTick(() => {
        this.skipOptionsWatch = false;
        this.searchFormacoes();
      });
    },

    getSortBy() {
      if (!this.options.sortBy || this.options.sortBy.length === 0) {
        return null;
      }

      return this.options.sortBy[0];
    },

    getSortDesc() {
      if (!this.options.sortDesc || this.options.sortDesc.length === 0) {
        return null;
      }

      return this.options.sortDesc[0] ? 1 : 0;
    },

    getSortIcon(column) {
      const currentSort = this.getSortBy();

      if (currentSort !== column) {
        return "mdi-swap-vertical";
      }

      const currentDesc = this.getSortDesc();

      return currentDesc ? "mdi-arrow-down" : "mdi-arrow-up";
    },

    clearSearch() {
      this.filter.search = null;
      this.options.page = 1;
      this.searchFormacoes();
    },

    resetFormacoes() {
      this.skipOptionsWatch = true;

      this.filter = {
        search: null,
      };

      this.options = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      };

      this.$nextTick(() => {
        this.skipOptionsWatch = false;
        this.searchFormacoes();
      });
    },

    deleteFormacao(item) {
      if (item.status === "completed") {
        return;
      }

      this.loading = true;

      Formacoes.delete(item.id)
        .then(() => {
          this.searchFormacoes();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getObjetivo(item) {
      if (!item) {
        return "—";
      }

      if (item.objetivo) {
        return item.objetivo;
      }

      const valores = item.valores || [];

      const objetivo = valores.find((valor) => {
        const campo = valor.campo || {};

        const key = String(campo.key || "").toLowerCase();
        const label = String(campo.label || "").toLowerCase();

        return key === "objetivo" || label === "objetivo";
      });

      if (!objetivo) {
        return "—";
      }

      return objetivo.valor_texto || objetivo.valor_json || "—";
    },

    getTipoAcaoLabel(value) {
      const labels = {
        acolhimento: "Acolhimento",
        sensibilizacao: "Sensibilização",
        formacao_local_trabalho: "Formação local de trabalho",
      };

      return labels[value] || value || "—";
    },

    getStatusLabel(status) {
      const labels = {
        draft: "Rascunho",
        signing: "Em assinatura",
        completed: "Finalizada",
        published: "Publicada",
      };

      return labels[status] || status || "—";
    },

    getStatusColor(status) {
      const colors = {
        draft: "grey",
        signing: "info",
        completed: "success",
        published: "info",
      };

      return colors[status] || "grey";
    },

    getTableDate(date) {
      return date != null ? dayjs(date).toDate().toLocaleDateString() : "-";
    },
  },
};
</script>

<style scoped>
.eva-page {
  padding-bottom: 32px;
}

.eva-top {
  margin-top: 8px;
}

.eva-breadcrumbs :deep(.v-breadcrumbs__item) {
  font-size: 13px;
}

.eva-titlebar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.eva-title {
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.2px;
}

.eva-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.06);
}

.eva-card__title {
  font-weight: 800;
  letter-spacing: 0.2px;
}

.eva-chip {
  border-radius: 999px;
}

.eva-table {
  border-radius: 14px;
  overflow: hidden;
}

.eva-table :deep(thead th) {
  font-weight: 800;
  color: rgba(0,0,0,0.75);
  letter-spacing: 0.2px;
}

.eva-table :deep(tbody tr:hover) {
  background: rgba(0,0,0,0.02);
}

.sortable-header {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
}

.sortable-header:hover {
  color: var(--v-primary-base);
}
</style>